import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import SubscriptionPlans from '../SubscriptionPlans/SubscriptionPlans';
import PaymentModal from '../PaymentModal/PaymentModal';
import TermsConditions from '../TermsConditions/TermsConditions';
import { authenticatedFetch } from '../Utils/apiHelpers';
import {
  PAYPAL_CURRENT_SUBSCRIPTION_URL,
  PAYPAL_PLAN_SWITCH_AUDIT_URL,
} from '../../config/api';
import './SubscriptionPage.css';

interface CurrentSubscription {
  active: boolean;
  plan_type: string;
  status: string;
  starts_at: string;
  expires_at: string;
}

interface AuditEvent {
  event_id: string;
  action: string;
  outcome: string;
  plan_type: string;
  amount: number | null;
  currency: string | null;
  created_at: string;
  details: {
    previous_payment_order_id?: number;
    current_plan?: string;
    switch_direction?: string;
    plan_switch?: boolean;
  };
}

interface AuditResponse {
  events: AuditEvent[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    has_more: boolean;
  };
}

const formatDate = (iso: string): string => {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatDateTime = (iso: string): string => {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [hasPurchasedPlan, setHasPurchasedPlan] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await authenticatedFetch(PAYPAL_CURRENT_SUBSCRIPTION_URL, {
          headers: { Accept: '*/*' },
        });

        if (response.ok) {
          const data = await response.json();
          const isActive = data.active === true || data.status === 'active' || data.status === 'COMPLETED';
          setHasPurchasedPlan(isActive);
          if (isActive) {
            setCurrentSubscription(data as CurrentSubscription);
          }
        }
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, []);

  // Fetch plan switch audit history for users with active subscriptions
  useEffect(() => {
    if (!hasPurchasedPlan) return;

    const fetchAudit = async () => {
      setAuditLoading(true);
      try {
        const response = await authenticatedFetch(PAYPAL_PLAN_SWITCH_AUDIT_URL(50, 0), {
          headers: { Accept: '*/*' },
        });

        if (response.ok) {
          const data: AuditResponse = await response.json();
          if (data.events && Array.isArray(data.events)) {
            setAuditEvents(data.events);
          }
        }
      } catch {
        // silently ignore — audit history is supplementary
      } finally {
        setAuditLoading(false);
      }
    };

    fetchAudit();
  }, [hasPurchasedPlan]);

  if (isLoading) {
    return (
      <div className="subscription-page">
        <div className="subscription-page-loading">
          <div className="subscription-page-spinner" />
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-page-header">
        <button
          className="subscription-back-btn"
          onClick={() => navigate('/home')}
          aria-label="Back to home"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>
        <h1 className="subscription-page-title">Subscription Plans</h1>
      </div>

      {hasPurchasedPlan && currentSubscription && (
        <div className="current-subscription-card" style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '12px',
          padding: '20px 24px',
          margin: '0 auto 24px',
          maxWidth: '800px',
          border: '1px solid #dee2e6',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: '0 0 6px', fontSize: '18px', color: '#2d3436' }}>
                Current Subscription
              </h3>
              <p style={{ margin: 0, color: '#636e72', fontSize: '14px' }}>
                Plan: <strong style={{ textTransform: 'capitalize' }}>{currentSubscription.plan_type?.toLowerCase()}</strong>
                {' · '}
                Status: <strong style={{ color: '#00b894' }}>{currentSubscription.status}</strong>
              </p>
              <p style={{ margin: '4px 0 0', color: '#636e72', fontSize: '14px' }}>
                Valid until: <strong>{formatDate(currentSubscription.expires_at)}</strong>
              </p>
            </div>
            <div style={{
              background: '#00b894',
              color: '#fff',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              Active
            </div>
          </div>
        </div>
      )}

      <SubscriptionPlans
        selectedPlan={selectedPlan}
        onSelectPlan={(plan) => setSelectedPlan(plan)}
        onContinue={() => setShowPaymentModal(true)}
        onShowTerms={() => setShowTerms(true)}
        hasPurchasedPlan={false}
      />

      {hasPurchasedPlan && (
        <div className="plan-switch-history" style={{
          maxWidth: '800px',
          margin: '0 auto 32px',
        }}>
          <h3 style={{
            fontSize: '18px',
            color: '#2d3436',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <RefreshCw size={18} />
            Plan Switch History
          </h3>

          {auditLoading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#636e72' }}>
              Loading history...
            </div>
          ) : auditEvents.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '24px',
              color: '#b2bec3',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}>
              No plan switches yet. Your subscription changes will appear here.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {auditEvents.map((event) => {
                const isUpgrade = event.details.switch_direction === 'upgrade';
                const isDowngrade = event.details.switch_direction === 'downgrade';
                const isSwitch = event.details.plan_switch === true;
                const outcomeColor = event.outcome === 'created' || event.outcome === 'completed'
                  ? '#00b894'
                  : event.outcome === 'not_approved' || event.outcome === 'failed'
                  ? '#e17055'
                  : '#636e72';

                return (
                  <div key={event.event_id} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '14px 16px',
                    background: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                  }}>
                    <div style={{
                      flexShrink: 0,
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: isUpgrade ? '#e3f6ed' : isDowngrade ? '#fdecea' : '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {isUpgrade ? (
                        <TrendingUp size={18} color="#00b894" />
                      ) : isDowngrade ? (
                        <TrendingDown size={18} color="#e17055" />
                      ) : (
                        <RefreshCw size={16} color="#636e72" />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#2d3436' }}>
                          {isSwitch ? 'Plan Switch' : event.action.replace(/_/g, ' ')}
                        </span>
                        {event.plan_type && (
                          <span style={{
                            fontSize: '12px',
                            color: '#636e72',
                            textTransform: 'capitalize',
                          }}>
                            → {event.plan_type.toLowerCase()}
                          </span>
                        )}
                        <span style={{
                          fontSize: '11px',
                          color: outcomeColor,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}>
                          {event.outcome.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#b2bec3' }}>
                        {formatDateTime(event.created_at)}
                        {event.amount != null && event.currency && (
                          <> · {event.currency} {event.amount.toFixed(2)}</>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showPaymentModal && (
        <PaymentModal
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            setHasPurchasedPlan(true);
            setSelectedPlan(null);
            navigate('/home');
          }}
          selectedPlan={selectedPlan || undefined}
        />
      )}

      {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}
    </div>
  );
};

export default SubscriptionPage;
